import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useGlobalLocation } from '@/contexts/LocationContext';
import { formatDateNL } from '@/lib/format';
import { CalendarClock, CheckCircle2, XCircle, Loader2, Inbox } from 'lucide-react';

const LEAVE_TYPE_LABEL = {
  vakantie: 'Vakantie', ziekte: 'Ziek', bijzonder_verlof: 'Bijzonder verlof',
  re_integratie: 'Re-integratie', onbetaald: 'Onbetaald verlof',
};

export default function Verlofaanvragen() {
  const { globalLocation } = useGlobalLocation();
  const scoped = globalLocation && globalLocation !== 'all' ? globalLocation : null;
  const [records, setRecords] = useState([]);
  const [locations, setLocations] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [feedback, setFeedback] = useState({ type: '', msg: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, locResp] = await Promise.all([
        api.get('/leave/pending', { params: scoped ? { location_id: scoped } : {} }),
        api.get('/locations'),
      ]);
      setRecords(data.records || []);
      const map = {};
      (locResp.data || []).forEach((l) => { map[l.slug] = l.name; });
      setLocations(map);
    } catch {
      setFeedback({ type: 'error', msg: 'Kon verlofaanvragen niet laden' });
    } finally {
      setLoading(false);
    }
  }, [scoped]);

  useEffect(() => { load(); }, [load]);

  const decide = async (rec, action) => {
    setBusyId(rec.id);
    try {
      await api.post(`/leave/${rec.id}/${action}`, {}, { headers: { 'If-Match': `"${rec.id}-${rec.version}"` } });
      setFeedback({ type: 'success', msg: action === 'approve' ? 'Aanvraag goedgekeurd' : 'Aanvraag afgewezen' });
      await load();
    } catch (e) {
      const status = e.response?.status;
      if (status === 409 || status === 412) { setFeedback({ type: 'error', msg: 'Vernieuwd: aanvraag is gewijzigd' }); await load(); }
      else setFeedback({ type: 'error', msg: 'Actie mislukt' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="page-verlofaanvragen">
      <div className="border-b border-[#e4e9f0] pb-4">
        <h2 className="text-3xl font-bold text-tw1-brand-navy flex items-center gap-2">
          <CalendarClock size={26} /> Verlofaanvragen
        </h2>
        <p className="mt-1 text-sm text-tw1-text-muted">
          Openstaande aanvragen ter goedkeuring{scoped ? ` — ${locations[scoped] || scoped}` : ' — alle locaties waartoe je toegang hebt'}.
        </p>
      </div>

      {feedback.msg && (
        <div className={`rounded-md px-3 py-2 text-sm ${feedback.type === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`} data-testid="aanvragen-feedback">
          {feedback.msg}
        </div>
      )}

      <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-700" data-testid="verlofsaldo-disclaimer">
        Indicatief saldo — opname wordt nog niet automatisch afgeboekt; het loonsysteem is leidend.
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Laden…</div>
        ) : records.length === 0 ? (
          <div className="p-12 text-center" data-testid="aanvragen-empty">
            <Inbox size={36} className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-500">Geen openstaande verlofaanvragen</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                  <th className="py-2 px-3">Medewerker</th>
                  <th className="py-2 px-3">Type</th>
                  <th className="py-2 px-3">Locatie</th>
                  <th className="py-2 px-3">Periode</th>
                  <th className="py-2 px-3 text-right">Uren</th>
                  <th className="py-2 px-3 text-right">Saldo</th>
                  <th className="py-2 px-3">Notitie</th>
                  <th className="py-2 px-3 text-right">Acties</th>
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id} data-testid={`aanvraag-row-${rec.id}`} className="border-b border-slate-100">
                    <td className="py-2 px-3 font-medium text-slate-900">
                      <Link to={`/medewerkers/${rec.employee_id}?tab=verlof`} className="hover:text-brand-navy hover:underline">
                        {rec.employee_name || rec.employee_id}
                      </Link>
                    </td>
                    <td className="py-2 px-3 text-slate-700">{LEAVE_TYPE_LABEL[rec.type] || rec.type}</td>
                    <td className="py-2 px-3 text-slate-600">{locations[rec.location_id] || rec.location_id}</td>
                    <td className="py-2 px-3 tabular-nums text-slate-700">{formatDateNL(rec.start_date)} – {formatDateNL(rec.end_date)}</td>
                    <td className="py-2 px-3 text-right tabular-nums">{rec.hours}</td>
                    <td className="py-2 px-3 text-right tabular-nums text-slate-500" data-testid={`aanvraag-saldo-${rec.id}`}>
                      {rec.employee_vacation_balance === null || rec.employee_vacation_balance === undefined ? '—' : rec.employee_vacation_balance}
                    </td>
                    <td className="py-2 px-3 text-slate-600">{rec.notes || '—'}</td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      <button onClick={() => decide(rec, 'approve')} disabled={busyId === rec.id} data-testid={`btn-approve-${rec.id}`}
                        className="mr-1 inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60">
                        <CheckCircle2 size={13} /> Goedkeuren
                      </button>
                      <button onClick={() => decide(rec, 'reject')} disabled={busyId === rec.id} data-testid={`btn-reject-${rec.id}`}
                        className="inline-flex items-center gap-1 rounded-md border border-rose-300 px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-60">
                        <XCircle size={13} /> Afwijzen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
